import { PERMISSIONS } from '@/consts/permissions';
import { env } from '@/env/env';

import type { User } from './schema';

// DEV-ONLY: the full set of HSQE permission keys the frontend gates UI actions on
// via `usePermission`. Derived from the canonical PERMISSIONS registry (every
// `HSQE_`-prefixed entry) so a new HSQE capability is granted automatically the
// moment it is added there — no second list to keep in sync.
export const HSQE_DEV_GRANT_PERMISSIONS: readonly string[] = Object.entries(PERMISSIONS)
  .filter(([key]) => key.startsWith('HSQE_'))
  .map(([, value]) => value);

/**
 * Is the DEV-ONLY HSQE full-access permission grant active?
 *
 * DOUBLE-GATED, and the first gate is non-negotiable:
 *  1. `import.meta.env.DEV` — Vite hardcodes this to `false` in every production
 *     build (`vite build`, which UAT and Production both use) and `true` only under
 *     the local dev server. The whole grant branch is therefore dead code, tree-shaken
 *     out of any production bundle. This is what makes shipping the bypass to prod
 *     impossible regardless of environment variables.
 *  2. `VITE_HSQE_DEV_GRANT_ALL === 'true'` — an explicit opt-in so the grant stays off
 *     by default even locally until a developer deliberately turns it on.
 */
export const isDevPermissionGrantActive = (): boolean => import.meta.env.DEV && env.VITE_HSQE_DEV_GRANT_ALL === 'true';

/**
 * Returns `user` decorated with the full HSQE permission set when the DEV-ONLY grant
 * is active; otherwise returns `user` untouched. Never mutates its argument.
 *
 * The grant flows through the REAL permission path: it appends the HSQE keys to
 * `user.Permissions`, exactly where a fully-permissioned whoAmI response would carry
 * them, so `hasPermission` runs its normal matching logic and every HSQE `usePermission`
 * gate un-greys. It deliberately does NOT short-circuit the checks (unlike a blanket
 * `return true`), so the real permission plumbing stays exercised for every other module.
 */
export const applyDevPermissionGrant = (user: User): User => {
  if (!isDevPermissionGrantActive()) return user;
  const existing = user.Permissions ?? [];
  return {
    ...user,
    Permissions: [...new Set([...existing, ...HSQE_DEV_GRANT_PERMISSIONS])]
  };
};
